/**
 * Lightweight per-tab navigation origin stack.
 *
 * Used by PageHeader (and any back-arrow surface) to know exactly where to
 * return to. More reliable than `location.state.from`, which is lost on
 * reload, `replace: true`, or programmatic `navigate(-1)`.
 *
 * Usage:
 *   pushNavOrigin(location.pathname + location.search);
 *   navigate("/explore-channels", { state: { from: location.pathname } });
 *
 *   // In PageHeader's back handler:
 *   const target = popNavOrigin();
 */

const KEY = "lov_nav_origin_stack_v1";
const MAX = 10;

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function read(): string[] {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function write(stack: string[]): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify(stack.slice(-MAX)));
  } catch {
    /* ignore quota errors */
  }
}

function isUsable(path: string | undefined | null): path is string {
  return typeof path === "string" && path.startsWith("/") && !path.startsWith("//");
}

/** Push the current page onto the origin stack before navigating away. */
export function pushNavOrigin(path: string): void {
  if (!isUsable(path)) return;
  const stack = read();
  if (stack[stack.length - 1] === path) return; // dedupe consecutive
  stack.push(path);
  write(stack);
}

/** Pop the most recent origin (used when handling a back action). */
export function popNavOrigin(): string | null {
  const stack = read();
  const top = stack.pop();
  write(stack);
  return isUsable(top) ? top : null;
}

/** Peek without mutating — useful for diagnostics or fallbacks. */
export function peekNavOrigin(): string | null {
  const stack = read();
  const top = stack[stack.length - 1];
  return isUsable(top) ? top : null;
}

/** Clear the entire stack. Call on sign-out or session reset. */
export function clearNavOrigin(): void {
  write([]);
}
